import { fetchApiGraphQL, type GraphQLError } from "@/utils/fetcher";
import { createEnrichmentIsland } from "@/utils/islands";
import { clearSession } from "@sitecorecomponents/Users/Store/tokenstore";
import { showFormMessage } from "../formMessages";
import { requiredRule, setupFieldValidation, setupPasswordToggle, setupPasswordChecklist, passwordRules } from "../formValidation";

const changePasswordMutation = `
  mutation ChangePassword($oldPassword: String!, $newPassword: String!) {
    changePassword(oldPassword: $oldPassword, newPassword: $newPassword)
  }
`;

type ChangePasswordResult = {
  data: {
    changePassword: boolean | null;
  };
};

function setupChangePasswordValidation(form: HTMLFormElement) {
  const oldPassword = form.querySelector<HTMLInputElement>('#OldPassword');
  const newPassword = form.querySelector<HTMLInputElement>('#NewPassword');

  if (oldPassword) {
    setupFieldValidation(oldPassword, [requiredRule]);
    setupPasswordToggle(oldPassword);
  }

  if (newPassword) {
    setupFieldValidation(newPassword, [requiredRule, { key: 'length', test: value => value.length >= 8 }]);
    setupPasswordToggle(newPassword);
    setupPasswordChecklist(newPassword, passwordRules);
  }
}

function collectFormData(form: HTMLFormElement) {
  return {
    oldPassword: form.querySelector<HTMLInputElement>('#OldPassword')?.value ?? '',
    newPassword: form.querySelector<HTMLInputElement>('#NewPassword')?.value ?? '',
  };
}

function validateForm(data: ReturnType<typeof collectFormData>): string | null {
  if (!data.oldPassword.trim() || !data.newPassword.trim()) {
    return null;
  }

  if (data.newPassword === data.oldPassword) {
    return 'same-as-old';
  }

  return null;
}

function messageKeyForError(message: string): string {
  const text = message.toLowerCase();

  if (text.includes('same as old') || text.includes('same as current')) return 'same-as-old';
  if (text.includes('password too short') || text.includes('minimum length')) return 'length';

  return 'failed';
}

async function handleSubmit(e: SubmitEvent, form: HTMLFormElement) {
  e.preventDefault();
  showFormMessage(form, null);

  const data = collectFormData(form);
  const validationError = validateForm(data);

  if (validationError) {
    showFormMessage(form, validationError);
    return;
  }

  const passwordValidation = passwordRules.find(rule => !rule.test(data.newPassword));
  if (passwordValidation) {
    showFormMessage(form, passwordValidation.key);
    return;
  }

  let result: ChangePasswordResult;
  try {
    result = await fetchApiGraphQL<ChangePasswordResult>(changePasswordMutation, {
      oldPassword: data.oldPassword,
      newPassword: data.newPassword,
    });
  } catch (err) {
    const message = (err as GraphQLError)?.errors?.[0]?.message ?? '';
    showFormMessage(form, messageKeyForError(message));
    return;
  }

  const success = result?.data?.changePassword;
  if (!success) {
    showFormMessage(form, 'failed');
    return;
  }

  showFormMessage(form, 'success');

  setTimeout(() => {
    clearSession();
    window.location.href = '/';
  }, 2000);
}

export default function init() {
  createEnrichmentIsland('.change-password-form', (elm) => {
    const form = elm as HTMLFormElement;

    setupChangePasswordValidation(form);

    form.addEventListener('submit', (e) => handleSubmit(e as SubmitEvent, form));
  });
}
